import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, PackageSearch } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { gsap, useGsap, scrollToTarget } from "@/lib/motion";
import { introState } from "@/components/motion/IntroLoader";

const HERO_BG =
  "https://images.pexels.com/photos/262353/pexels-photo-262353.jpeg?auto=compress&cs=tinysrgb&w=2000&h=1300&fit=crop";
const HERO_BG_SM =
  "https://images.pexels.com/photos/262353/pexels-photo-262353.jpeg?auto=compress&cs=tinysrgb&w=900&h=1400&fit=crop";

/* Route ticker cities, always shown in the visitor's language. */
const ROUTE = ["istanbul", "gaziantep", "aleppo", "damascus", "guangzhou", "yiwu", "mersin", "latakia"];

export default function Hero() {
  const { t, lang } = useLang();
  const isRtl = lang === "ar";
  const root = useRef(null);

  useGsap(
    () => {
      const delay = introState.playing ? introState.duration : 0.1;
      const tl = gsap.timeline({ delay });
      tl.from(".hero-photo", { scale: 1.18, opacity: 0, duration: 1.8, ease: "expo.out" })
        .from(".hero-line > span", { yPercent: 110, duration: 1.1, stagger: 0.12 }, 0.15)
        .from(".hero-kicker", { opacity: 0, y: 14, duration: 0.8 }, 0.1)
        .from(".hero-fade", { opacity: 0, y: 24, duration: 0.9, stagger: 0.08 }, 0.55)
        .from(".hero-ticker", { opacity: 0, duration: 1.2 }, 0.9);

      // Scroll away: the photo drifts slower than the page, the title lifts and dims.
      gsap.to(".hero-photo-wrap", {
        yPercent: 18,
        ease: "none",
        scrollTrigger: { trigger: root.current, start: "top top", end: "bottom top", scrub: true },
      });
      gsap.to(".hero-content", {
        yPercent: -14,
        opacity: 0.15,
        ease: "none",
        scrollTrigger: { trigger: root.current, start: "35% top", end: "bottom top", scrub: true },
      });
    },
    [lang],
    root,
  );

  return (
    <section
      ref={root}
      data-testid="hero-section"
      className="on-dark relative isolate overflow-hidden bg-[var(--forest-950)] min-h-[100svh] flex flex-col"
    >
      {/* Photograph, toned into the brand green */}
      <div aria-hidden className="hero-photo-wrap absolute inset-0 -z-10 will-change-transform">
        <picture>
          <source media="(max-width: 767px)" srcSet={HERO_BG_SM} />
          <img
            src={HERO_BG}
            alt=""
            className="hero-photo w-full h-full object-cover mix-blend-luminosity opacity-55"
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
        </picture>
        <div className="absolute inset-0 bg-[linear-gradient(180deg,oklch(0.17_0.028_140/0.65)_0%,oklch(0.17_0.028_140/0.2)_38%,oklch(0.17_0.028_140/0.92)_82%,var(--forest-950)_100%)]" />
        <div className="absolute inset-0 dot-grid opacity-60 [mask-image:linear-gradient(180deg,transparent,black_60%)]" />
      </div>

      <div className="hero-content container-x w-full relative flex-1 flex flex-col justify-end pt-32 pb-14 lg:pb-20">
        <p className="hero-kicker flex items-center gap-3 text-[15px] font-semibold text-[var(--on-dark-muted)]">
          <span className="relative flex h-2.5 w-2.5" aria-hidden>
            <span className="absolute inset-0 rounded-full bg-[var(--signal)] opacity-60 motion-safe:animate-ping" />
            <span className="relative h-2.5 w-2.5 rounded-full bg-[var(--signal)]" />
          </span>
          {t.hero.kicker}
        </p>

        <h2
          className="type-display mt-6 text-[clamp(2.6rem,7.4vw,6rem)] text-[var(--on-dark)] max-w-[15ch]"
          data-testid="hero-title"
        >
          {t.hero.headline.map((line, i) => (
            <span key={`${lang}-${i}`} className="hero-line block overflow-hidden pb-[0.06em]">
              <span className={`block ${i === 1 ? "text-[var(--forest-300)]" : ""}`}>{line}</span>
            </span>
          ))}
        </h2>

        <div className="mt-8 grid gap-8 lg:grid-cols-12 lg:items-end">
          <p className="hero-fade lg:col-span-6 text-lg lg:text-xl leading-relaxed text-[var(--on-dark-muted)] max-w-[52ch]">
            {t.hero.lede}
          </p>

          <div className="hero-fade lg:col-span-6 flex flex-wrap items-center gap-3 lg:justify-end">
            <button
              type="button"
              onClick={() => scrollToTarget("#track")}
              data-testid="hero-cta-track"
              className="btn-primary h-14 px-7 rounded-full text-base gap-2.5 group"
            >
              <PackageSearch className="w-5 h-5" aria-hidden />
              {t.hero.ctaTrack}
            </button>
            <Link
              to="/contact"
              data-testid="hero-cta-quote"
              className="h-14 px-7 rounded-full text-base font-bold inline-flex items-center gap-2 border border-white/25 text-[var(--on-dark)] hover:border-[var(--signal)] hover:text-[var(--signal)] transition-colors duration-300 group"
            >
              {t.hero.ctaQuote}
              <ArrowRight
                className={`w-5 h-5 transition-transform duration-500 ease-out-expo group-hover:translate-x-1 ${isRtl ? "rtl-flip group-hover:-translate-x-1" : ""}`}
                aria-hidden
              />
            </Link>
          </div>
        </div>

        <button
          type="button"
          onClick={() => scrollToTarget("#track")}
          className="hero-fade mt-14 hidden md:inline-flex items-center gap-2 self-start text-sm text-[var(--on-dark-muted)] hover:text-[var(--on-dark)] transition-colors"
        >
          <ArrowDown className="w-4 h-4 motion-safe:animate-bounce" aria-hidden />
          {t.hero.scroll}
        </button>
      </div>

      {/* Route ticker */}
      <div className="hero-ticker relative border-t border-white/10 bg-[oklch(0.17_0.028_140/0.6)] overflow-hidden" aria-hidden>
        <div className="flex w-max animate-marquee [--marquee-duration:55s] py-3.5">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center shrink-0">
              {ROUTE.map((c, i) => (
                <span key={`${dup}-${c}`} className="flex items-center text-sm font-semibold tracking-wide text-[var(--on-dark-muted)]">
                  <span className="px-6">{t.cities[c]}</span>
                  <span className={i % 3 === 2 ? "text-[var(--signal)]" : "text-white/30"}>{i % 3 === 2 ? "◆" : isRtl ? "←" : "→"}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
